import { AddressFields } from 'ui-kit/form/interfaces/AddressModal';
import { handleError } from 'utilities/utilities';
import { IVerifyAddressResponse } from '../file/interfaces/verify-address';
import { getApiHost } from '../host-by-env';
import {
  TitleMonitoringInvite,
  TitleMonitoringSignupDTO,
  TitleMonitoringEnrollment,
  TitleMonitoringConfig,
  TitleMonitoringConfigUpsert,
  TitleMonitoringFlyer,
  TitleMonitoringLogoUpload,
  TitleMonitoringLogoUploadResult,
} from './types';

export async function titleMonitoringSignup(payload: TitleMonitoringSignupDTO): Promise<boolean> {
  try {
    const url = `${getApiHost()}jacket/title-monitoring/enrollment`;
    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await response.json();
    handleError(json);
    return json.result?.titleMonitoringEnrollmentId != null;
  } catch (error) {
    console.error('Error in titleMonitoringSignup', error);
    return false;
  }
}

export async function getTitleMonitoringInviteByInvitationGuid(
  invitationGuid: string,
): Promise<TitleMonitoringInvite | null> {
  try {
    const url = `${getApiHost()}jacket/title-monitoring/invite?invitationGuid=${invitationGuid}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await response.json();
    handleError(json);
    return json.result;
  } catch (error) {
    console.error('Error in getTitleMonitoringInviteByInvitationGuid', error);
    return null;
  }
}

export async function getTitleMonitoringInviteByPropertyId(propertyId: number): Promise<TitleMonitoringInvite | null> {
  try {
    const url = `${getApiHost()}jacket/title-monitoring/invite?propertyId=${propertyId}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await response.json();
    handleError(json);
    return json.result;
  } catch (error) {
    console.error('Error in getTitleMonitoringInviteByPropertyId', error);
    return null;
  }
}

export async function getTitleMonitoringInviteByFirmIdAndPropertyDetailId(
  firmId: number,
  propertyDetailId: string | number,
): Promise<TitleMonitoringInvite | null> {
  try {
    const url = `${getApiHost()}jacket/title-monitoring/invite?firmId=${encodeURIComponent(
      String(firmId),
    )}&propertyDetailId=${encodeURIComponent(String(propertyDetailId))}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await response.json();
    handleError(json);
    return json.result;
  } catch (error) {
    console.error('Error in getTitleMonitoringInviteByFirmIdAndPropertyDetailId', error);
    return null;
  }
}

/* Anonymous counterpart of verifyAddress in file-api for the public Title Monitoring signup page.
   Returns null when the call fails so callers can tell a failure apart from an unmatched address. */
export async function verifyTitleMonitoringPropertyAddress(
  address: AddressFields,
): Promise<IVerifyAddressResponse[] | null> {
  try {
    const url = `${getApiHost()}file/verifyAddress`;
    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify({
        address1: address.address1,
        address2: address.address2,
        city: address.city,
        state: address.stateOrProvince,
        postalCode: address.postalCode,
        verifyAddressType: 'address',
      }),
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await response.json();
    const { result } = json;
    handleError(json);
    return result.map((item: any) => ({
      address1: item.Address1,
      address2: item.Address2,
      city: item.City,
      zip: item.Zip,
      county: item.County,
      apn: item.Apn,
      propertyDetailId: item.PropertyDetailId,
      stateCode: item.StateCode,
    }));
  } catch (error) {
    console.error('Error in verifyTitleMonitoringPropertyAddress', error);
    return null;
  }
}

export async function getTitleMonitoringEnrollment(
  propertyDetailId: string | number,
  email: string,
): Promise<TitleMonitoringEnrollment | null> {
  try {
    const url = `${getApiHost()}jacket/title-monitoring/enrollment?propertyDetailId=${encodeURIComponent(
      String(propertyDetailId),
    )}&email=${encodeURIComponent(email)}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await response.json();
    handleError(json);
    return json.result;
  } catch (error) {
    console.error('Error in getTitleMonitoringEnrollment', error);
    return null;
  }
}

export async function getTitleMonitoringConfig(params: {
  firmId?: string | number;
  firmGuid?: string;
}): Promise<TitleMonitoringConfig | null> {
  try {
    const query = params.firmGuid
      ? `firmGuid=${encodeURIComponent(params.firmGuid)}`
      : `firmId=${encodeURIComponent(String(params.firmId))}`;
    const url = `${getApiHost()}jacket/title-monitoring/config?${query}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await response.json();
    handleError(json);
    return json.result;
  } catch (error) {
    console.error('Error in getTitleMonitoringConfig', error);
    return null;
  }
}

export async function getTitleMonitoringFlyer(firmGuid: string, token: string): Promise<TitleMonitoringFlyer | null> {
  const url = `${getApiHost()}jacket/title-monitoring/flyer?firmGuid=${encodeURIComponent(firmGuid)}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getTitleMonitoringFlyerByPropertyId(
  propertyId: number,
  token: string,
): Promise<TitleMonitoringFlyer | null> {
  const url = `${getApiHost()}jacket/title-monitoring/flyer?propertyId=${propertyId}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function saveTitleMonitoringConfig(
  payload: TitleMonitoringConfigUpsert,
  token: string,
): Promise<TitleMonitoringConfig | null> {
  const url = `${getApiHost()}jacket/title-monitoring/config`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function uploadTitleMonitoringLogo(
  payload: TitleMonitoringLogoUpload,
  token: string,
): Promise<TitleMonitoringLogoUploadResult | null> {
  const url = `${getApiHost()}jacket/title-monitoring/logo`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function deleteTitleMonitoringLogo(firmId: number, token: string): Promise<boolean> {
  const url = `${getApiHost()}jacket/title-monitoring/logo?firmId=${encodeURIComponent(String(firmId))}`;
  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  const json = await response.json();
  handleError(json);
  return (json.result ?? json.Result) === true;
}
